import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Captions, Play, Volume2, VolumeX } from "lucide-react";
import type { ServedAd } from "@/api/types";
import { cn } from "@/lib/cn";
import { useMedia } from "@/lib/useMedia";
import { useReducedMotion } from "@/motion/useReducedMotion";
import { trackClick, trackVideo, useImpression } from "./api";
import { SponsoredTag } from "./Sponsored";
import { videoSources } from "./videoSources";

type HlsLike = { loadSource(url: string): void; attachMedia(el: HTMLMediaElement): void; destroy(): void };

/** True when the visitor asked for less data (Save-Data / 2G): show the poster, play only on tap. */
function saveData(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !!c && (c.saveData === true || /(^|-)2g$/.test(c.effectiveType ?? ""));
}

export function AdCta({ ad, className, children }: { ad: ServedAd; className?: string; children?: ReactNode }) {
  const label = children ?? (ad.cta_label || "Learn more");
  const cls = cn(
    "inline-flex min-h-10 items-center justify-center rounded-full bg-[var(--primary-strong)] px-5 text-sm font-semibold text-white shadow-glow-2",
    className,
  );
  return ad.external ? (
    <a href={ad.cta_url} target="_blank" rel="noopener sponsored" className={cls} onClick={() => trackClick(ad.token)}>
      {label}
    </a>
  ) : (
    <Link to={ad.cta_url} className={cls} onClick={() => trackClick(ad.token)}>
      {label}
    </Link>
  );
}

export interface AdVideoProps {
  ad: ServedAd;
  /** Card shows headline, text and CTA under the video; hero/story render only the video (the parent adds text). */
  variant?: "card" | "hero" | "story" | "bare";
  /** Carousels: only the active slide may play. */
  active?: boolean;
  /** Stories: play regardless of reduced motion (the viewer opened it on purpose). */
  forcePlay?: boolean;
  /** Start with sound (stories after a tap). Autoplay is always muted. */
  startMuted?: boolean;
  /** Pause from outside (stories: hold to pause). */
  paused?: boolean;
  className?: string;
  /** Aspect box, e.g. "aspect-square sm:aspect-video". */
  boxClassName?: string;
  onEnded?: () => void;
  onProgress?: (fraction: number) => void;
}

/**
 * Video ad: muted autoplay only while at least half of it is on screen, paused when scrolled away, poster
 * until it can play, HLS (hls.js where Safari's native HLS isn't there) with an MP4 fallback, captions,
 * an unmute button, a "Sponsored" label and the call to action. Reduced motion or Save-Data: poster plus a
 * play button instead of autoplay. Reports impression, view (2 s watched while visible), quartiles and clicks.
 */
export function AdVideo({
  ad,
  variant = "card",
  active = true,
  forcePlay = false,
  startMuted = true,
  paused = false,
  className,
  boxClassName,
  onEnded,
  onProgress,
}: AdVideoProps) {
  const mobile = !useMedia("(min-width: 641px)");
  const reduced = useReducedMotion();
  const box = useImpression<HTMLDivElement>(ad.token);
  const video = useRef<HTMLVideoElement>(null);
  const hls = useRef<HlsLike | null>(null);
  const watched = useRef(0);
  const lastTime = useRef<number | null>(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false); // sources attached (only once first visible: never competes with LCP)
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(startMuted);
  const [captions, setCaptions] = useState(false);
  const [userStarted, setUserStarted] = useState(false);
  const src = videoSources(ad, mobile);
  const autoplay = forcePlay || (!reduced && !saveData());
  const shouldPlay = active && !paused && visible && (autoplay || userStarted);

  // Visible = at least 50% on screen.
  useEffect(() => {
    const el = box.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => setVisible(!!entry && entry.intersectionRatio >= 0.5), { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, [box]);

  useEffect(() => {
    if (visible && active) setLoaded(true);
  }, [visible, active]);

  // Attach the stream once.
  useEffect(() => {
    const el = video.current;
    if (!el || !loaded) return;
    let cancelled = false;
    const native = !!src.hls && el.canPlayType("application/vnd.apple.mpegurl") !== "";
    if (native) {
      el.src = src.hls;
    } else if (src.hls) {
      // The light build (no alt-audio, subtitles or DRM): captions use a native <track>, and it keeps the
      // lazy chunk inside the 120 KB performance budget.
      void import("hls.js/dist/hls.light.mjs")
        .then(({ default: Hls }) => {
          if (cancelled) return;
          if (Hls.isSupported()) {
            const h = new Hls({ capLevelToPlayerSize: true, maxBufferLength: 10 }) as unknown as HlsLike;
            h.loadSource(src.hls);
            h.attachMedia(el);
            hls.current = h;
          } else {
            el.src = src.mp4;
          }
        })
        .catch(() => {
          if (!cancelled) el.src = src.mp4;
        });
    } else {
      el.src = src.mp4;
    }
    return () => {
      cancelled = true;
      hls.current?.destroy();
      hls.current = null;
    };
  }, [loaded, src.hls, src.mp4]);

  useEffect(() => {
    const el = video.current;
    if (!el || !loaded) return;
    if (shouldPlay) {
      el.muted = muted;
      void el.play().catch(() => {
        // Autoplay refused (e.g. data saver): fall back to tap-to-play.
        setPlaying(false);
      });
    } else if (!el.paused) {
      el.pause();
    }
  }, [shouldPlay, loaded, muted]);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    for (const track of Array.from(el.textTracks)) track.mode = captions ? "showing" : "hidden";
  }, [captions, loaded]);

  const onTime = useCallback(() => {
    const el = video.current;
    if (!el || !el.duration) return;
    const t = el.currentTime;
    if (lastTime.current !== null && visible && t > lastTime.current && t - lastTime.current < 1.5) watched.current += t - lastTime.current;
    lastTime.current = t;
    if (watched.current >= 2) trackVideo(ad.token, "view");
    const f = t / el.duration;
    if (f >= 0.25) trackVideo(ad.token, "q25");
    if (f >= 0.5) trackVideo(ad.token, "q50");
    if (f >= 0.75) trackVideo(ad.token, "q75");
    if (f >= 0.98) trackVideo(ad.token, "q100");
    onProgress?.(f);
  }, [ad.token, visible, onProgress]);

  const loop = variant !== "story" && (ad.duration_s ?? 0) > 0 && (ad.duration_s ?? 0) <= 20;
  const showPlayButton = !playing && !autoplay && !userStarted;

  const player = (
    <div ref={box} className={cn("relative overflow-hidden bg-black", boxClassName)}>
      {src.poster && !playing && <img src={src.poster} alt="" className="absolute inset-0 size-full object-cover" decoding="async" />}
      {/* Captions are optional for advertisers: a <track> is added whenever they upload a .vtt file. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={video}
        className="absolute inset-0 size-full object-cover"
        poster={src.poster || undefined}
        muted={muted}
        playsInline
        preload="none"
        loop={loop}
        disablePictureInPicture
        aria-label={ad.headline ? `Video ad: ${ad.headline}` : "Video ad"}
        onPlaying={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={onTime}
        onEnded={() => {
          trackVideo(ad.token, "q100");
          onEnded?.();
        }}
        crossOrigin="anonymous"
      >
        {ad.captions_url && <track kind="captions" src={ad.captions_url} srcLang="en" label="English" />}
      </video>
      {showPlayButton && (
        <button
          type="button"
          onClick={() => {
            setUserStarted(true);
            setLoaded(true);
          }}
          className="absolute inset-0 grid place-items-center bg-black/20"
          aria-label="Play video ad"
        >
          <span className="grid size-14 place-items-center rounded-full bg-void/80 text-white shadow-glow-2">
            <Play className="size-6 translate-x-0.5 fill-current" aria-hidden />
          </span>
        </button>
      )}
      {(variant === "card" || variant === "bare") && <SponsoredTag className="pointer-events-none absolute left-3 top-3" />}
      <div className={cn("absolute z-10 flex gap-2", variant === "story" ? "right-3 top-[calc(var(--safe-top)+80px)]" : "right-3 top-3")}>
        {ad.captions_url && (
          <button
            type="button"
            onClick={() => setCaptions((c) => !c)}
            aria-pressed={captions}
            className="grid size-9 place-items-center rounded-full bg-scrim/70 text-white backdrop-blur"
            aria-label={captions ? "Hide captions" : "Show captions"}
          >
            <Captions className="size-4" aria-hidden />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setMuted((m) => !m);
            if (!playing) setUserStarted(true);
          }}
          className="grid size-9 place-items-center rounded-full bg-scrim/70 text-white backdrop-blur"
          aria-label={muted ? "Turn sound on" : "Turn sound off"}
        >
          {muted ? <VolumeX className="size-4" aria-hidden /> : <Volume2 className="size-4" aria-hidden />}
        </button>
      </div>
    </div>
  );

  if (variant !== "card") return <div className={className}>{player}</div>;

  return (
    <div className={cn("overflow-hidden rounded-card border border-fg/[0.06] bg-surface shadow-glow-1", className)}>
      {player}
      <div className="flex flex-wrap items-center gap-3 p-4">
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-semibold text-ink">{ad.headline}</p>
          {ad.body && <p className="mt-0.5 text-sm text-ink-muted">{ad.body}</p>}
          <p className="mt-1 text-xs text-ink-muted">{ad.advertiser}</p>
        </div>
        <AdCta ad={ad} />
      </div>
    </div>
  );
}
